import type { Turn, ToolCallState } from '@microsoft/agent-host-protocol';
import type { AgentPlannedFile, AgentPlannedFiles, AgentThreadView } from '@/_contract/agentChat';
import { isRepositoryPath } from '@/_pure/agentChanges';

export const PLANNED_FILES_TOOL = 'repository_plan_files';
export const PLANNED_FILES_FORMAT = 'cosmos.planned-files.v1';

export function isPlannedFileList(value: unknown): value is AgentPlannedFile[] {
    return Array.isArray(value) && value.length <= 20 && value.every(file =>
        !!file && typeof file === 'object' && Object.keys(file).every(key => ['path', 'intent'].includes(key))
        && 'path' in file && isRepositoryPath(file.path) && file.path.length <= 1024
        && 'intent' in file && ['create', 'modify', 'delete'].includes(file.intent))
        && new Set(value.map(file => file.path)).size === value.length;
}

export function isAgentPlannedFiles(value: unknown): value is AgentPlannedFiles {
    return !!value && typeof value === 'object'
        && 'turnId' in value && typeof value.turnId === 'string'
        && 'current' in value && typeof value.current === 'boolean'
        && 'status' in value && ['ready', 'loading', 'error'].includes(String(value.status))
        && 'files' in value && isPlannedFileList(value.files)
        && (value.status === 'ready' || value.files.length === 0)
        && (value.status !== 'error' || 'error' in value && typeof value.error === 'string' && value.error.length > 0)
        && (!('observedAt' in value) || value.observedAt === undefined || typeof value.observedAt === 'string')
        && (!('error' in value) || value.error === undefined || typeof value.error === 'string');
}

export function projectPlannedFiles(turn: Pick<Turn, 'id' | 'responseParts'>, current: boolean, worktreeId?: string): AgentPlannedFiles | undefined {
    const tool = turn.responseParts.findLast(part => part.kind === 'toolCall'
        && part.toolCall.toolName === PLANNED_FILES_TOOL && part.toolCall.contributor?.kind === 'client');
    if (tool?.kind !== 'toolCall') return undefined;
    const call: ToolCallState = tool.toolCall;
    const base = { turnId: turn.id, current, files: [] };
    if (!('state' in turn) && (call.status === 'streaming' || call.status === 'pending-confirmation' || call.status === 'running')) {
        return { ...base, status: 'loading' };
    }
    const failed = { ...base, status: 'error' as const, error: 'Planned-file declaration unavailable or invalid for this worktree. Ask the Agent to declare it again.' };
    if (call.status !== 'completed' || !call.success || !worktreeId) return failed;
    try {
        const content = call.content;
        if (content?.length !== 1 || content[0].type !== 'text' || content[0].text.length > 32_000) return failed;
        const value: unknown = JSON.parse(content[0].text);
        if (!value || typeof value !== 'object'
            || !('format' in value) || value.format !== PLANNED_FILES_FORMAT
            || !('worktreeId' in value) || value.worktreeId !== worktreeId
            || !('files' in value) || !isPlannedFileList(value.files)
            || !('observedAt' in value) || typeof value.observedAt !== 'string'
            || !Number.isFinite(Date.parse(value.observedAt))) return failed;
        return { ...base, status: 'ready', files: value.files, observedAt: value.observedAt };
    } catch (error) {
        if (!(error instanceof SyntaxError)) throw error;
        return failed;
    }
}

export function selectedPlannedFiles(state: {
    view: AgentThreadView | null; changesetId?: string; loading: boolean; reconnecting?: boolean;
}): AgentPlannedFiles | undefined {
    if (state.loading || state.reconnecting || state.view?.transport !== 'ahp' || state.changesetId !== 'planned') return undefined;
    return state.view.plannedFiles?.find(files => files.current);
}

export function plannedFilesSummary(files?: AgentPlannedFiles): string {
    return [
        'Planned files (latest turn) - intentions, not actual edits.',
        !files ? 'No structured declaration in the latest turn. Paths are never inferred from prose or earlier plans.'
            : files.status === 'loading' ? 'Declaration in progress; planned highlights are suspended.'
                : files.status === 'error' ? files.error
                    : `${files.files.length} declared file(s).${files.files.length ? '' : ' The Agent explicitly cleared the declared scope.'}`,
        files?.observedAt ? `Declared: ${files.observedAt}. Not a guarantee of current existence or complete scope.` : '',
        'Declarations replace the turn scope and may evolve. Not approval, implementation or scheduling.',
        'Core-space: violet exact files; pink containing folders for new/hidden files. Layout filters remain unchanged.',
    ].filter(Boolean).join('\n');
}
